import {
  FormProvider,
  Path,
  useForm,
  type SubmitErrorHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dynamic from 'next/dynamic';

import { type OnSubmitHandler } from '../../../../types/forms.typed';
import {
  type CloseBrothersForm,
  closebrothersFormSchema,
  type QueryParameters,
  InitialFormValues,
  closeBrothersCollapsiblePanelSchema,
  filterValidPanelNames,
} from '../../../../components/CarFinancePage/queries/schema/carFinance.schema';
import { SubmitPanel } from '../shared/SubmitPanel/SubmitPanel';
import { ResidentialDetails } from './panels/ResidentialDetails/ResidentialDetails';
import { EmploymentDetails } from './panels/EmploymentDetails/EmploymentDetails';
import { FinanceCalculator } from '../../../../components/CarFinancePage/components/FinanceCalculator/FinanceCalculator';
import { PersonalDetails } from '../../../../components/CarFinancePage/components/Panels/PersonalDetails/PersonalDetails';
import { BankingInformation } from './panels/BankingInformation/BankingInformation';
import { DataProtectionAndConsents } from '../../../../components/CarFinancePage/financeProviders/closebrothers/panels/DataProtectionAndConsents/DataProtectionAndConsents';

import { useSubmitFinanceForm } from '../../../../components/CarFinancePage/queries/hooks/useSubmitFinanceForm';
import { usePanelDispatch } from '../shared/PanelContext';
import { TotalDepositProvider } from '../../contexts/TotalDepositContext';

import { Spacer } from '../../../../helpers/genericStyles';
import { formatDate } from '../formatting';
import {
  clearableRequiredFieldsDefaultValuesBankingDetails,
  clearableRequiredFieldsDefaultValuesCurrentAddress,
  clearableRequiredFieldsDefaultValuesEmploymentDetails,
  clearableRequiredFieldsDefaultValuesPersonalDetails,
  clearableRequiredFieldsDefaultValuesPreviousAddress,
  requiredDefaultValuesLoanDetails,
} from '../shared/constants/clearableRequiredDefaultValues';
import { THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS } from './panels/ResidentialDetails/constants';

import type { UsePromptUserOnChangeBeforeLeavingModalParams } from '../../../../components/CarFinancePage/components/PromptUserOnChangeBeforeLeavingModal/PromptUserOnChangeBeforeLeavingModal.typed';
import { formatValidationErrors } from '../utils/formatValidationErrors/formatValidationErrors';
import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';
import { FEATURE_CAR_FINANCE_UPDATES } from '../../../../utils';
import { FinanceCalculatorV2 } from '../../components/FinanceCalculatorV2/FinanceCalculatorV2';

const PromptUserOnChangeBeforeLeavingModal =
  dynamic<UsePromptUserOnChangeBeforeLeavingModalParams>(
    import(
      '../../../../components/CarFinancePage/components/PromptUserOnChangeBeforeLeavingModal/PromptUserOnChangeBeforeLeavingModal'
    ).then((mod) => mod.PromptUserOnChangeBeforeLeavingModal),
  );

type CloseBrothersFinanceFormProps = {
  query: QueryParameters;
  onSubmitCallback: () => void;
  onSubmitErrorCallback: (errorFields: Array<string>) => void;
  initialFormData: InitialFormValues;
  adId: string;
};

const CloseBrothersFinanceForm = (props: CloseBrothersFinanceFormProps) => {
  const {
    query,
    onSubmitCallback,
    onSubmitErrorCallback,
    initialFormData,
    adId,
  } = props;
  const methods = useForm<CloseBrothersForm>({
    resolver: zodResolver(closebrothersFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      /**
       * We initialise the form with these values to determine what constitutes an invalid value for a field.
       * A panel with one or more invalid field values will be considered "incomplete".
       *
       * Once input is provided, it is compared to these values. If all the required values for a particular panel
       * are valid, that panel is then considered "complete".
       */
      personalDetails: {
        ...clearableRequiredFieldsDefaultValuesPersonalDetails,
        countryOrigin: '',
        numberOfDependents: '',
      },
      employmentDetails: {
        ...clearableRequiredFieldsDefaultValuesEmploymentDetails,
        industry: '',
        type: '',
        employerPhone: '',
        employerAddress: {
          addressLine1: '',
          addressLine2: '',
          town: '',
          county: '',
        },
      },
      bankingDetails: {
        ...clearableRequiredFieldsDefaultValuesBankingDetails,
        yearsWithBank: '',
      },
      previousAddress: {
        ...clearableRequiredFieldsDefaultValuesPreviousAddress,
        town: '',
        timeAtAddress: {
          years: '',
        },
      },
      currentAddress: {
        ...clearableRequiredFieldsDefaultValuesCurrentAddress,
        town: '',
      },
      loanDetails: {
        ...initialFormData.loanDetails,
        ...requiredDefaultValuesLoanDetails,
      },
      consent: {
        dataConsent: false,
      },
      carDetails: initialFormData.carDetails,
      hiddenFields: initialFormData.hiddenFields,
    },
  });

  const dispatch = usePanelDispatch();

  const validateCloseBrothersErrors = (errors: Array<string>) => {
    return filterValidPanelNames(closeBrothersCollapsiblePanelSchema, errors);
  };

  const { mutate, isPending } = useSubmitFinanceForm({
    onValidationError: (validationErrors) => {
      for (const error of validationErrors) {
        // Non-trivial to correctly infer the type of the error.field
        // No error thrown when setting error on a field that does not exist
        // Not ideal but does not break the form
        methods.setError(error.field as Path<CloseBrothersForm>, {
          message: error.message,
        });
      }
      // Scroll the top error into view
      methods.setFocus(validationErrors[0].field as Path<CloseBrothersForm>);
      const fieldsWithErrors = validationErrors.map((error) => error.field);
      const panelsWithErrors = validateCloseBrothersErrors(
        formatValidationErrors(fieldsWithErrors),
      );
      dispatch({
        type: 'OPEN_PANELS',
        panels: panelsWithErrors,
      });
      onSubmitErrorCallback(fieldsWithErrors);
    },
    onSuccess: onSubmitCallback,
  });

  const onSubmit: OnSubmitHandler<typeof methods> = async (values) => {
    const formattedValues = {
      ...values,
      personalDetails: {
        ...values.personalDetails,
        dateOfBirth: formatDate(
          values.personalDetails.dateOfBirth.toLocaleDateString('en-IE'),
        ),
      },
      /**
       * We exclude the previousAddress fields entirely since Close Brothers expects an empty object
       * rather than fields with empty values if no previous address is applicable
       */
      previousAddress: {
        ...(parseInt(values.currentAddress.timeAtCurrentAddress.years) <=
        THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS
          ? values.previousAddress
          : {}),
      },
    };

    mutate(formattedValues);
  };

  const onInvalidCloseBrothersSubmission: SubmitErrorHandler<
    typeof methods
  > = async (errors) => {
    const fieldsWithErrors = typeSafeObjectKeys(errors);
    const panelsWithErrors = validateCloseBrothersErrors(fieldsWithErrors);
    dispatch({
      type: 'OPEN_PANELS',
      panels: panelsWithErrors,
    });
    onSubmitErrorCallback(fieldsWithErrors);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(
            onSubmit,
            onInvalidCloseBrothersSubmission,
          )}
          noValidate
        >
          <TotalDepositProvider minimumDepositValue={query.minimumDepositValue}>
            {FEATURE_CAR_FINANCE_UPDATES ? (
              <FinanceCalculatorV2
                dealerId={query.dealerId}
                financeHouse={query.provider}
                minimumDepositPercentage={query.minimumDepositPercentage}
                minimumDepositValue={query.minimumDepositValue}
                adId={adId}
                showOutstandingFinance
              />
            ) : (
              <FinanceCalculator
                dealerId={query.dealerId}
                financeHouse={query.provider}
                minimumDepositPercentage={query.minimumDepositPercentage}
                minimumDepositValue={query.minimumDepositValue}
                showOutstandingFinance
              />
            )}
            <Spacer marginBottom={['S8', 'M16']} />
            <PersonalDetails
              displayCountryOfBirth
              displayMaidenName
              displayNumberOfDependants
              displayCurrentVehicleRegistrationNumber
            />
            <ResidentialDetails />
            <EmploymentDetails />
            <BankingInformation />
            <DataProtectionAndConsents />
            <SubmitPanel isSubmitting={isPending} />
            <Spacer marginBottom="L40" />
          </TotalDepositProvider>
        </form>
      </FormProvider>
      <PromptUserOnChangeBeforeLeavingModal
        promptUser={methods.formState.isDirty}
      />
    </>
  );
};

export { CloseBrothersFinanceForm };
